import type { CSSProperties, ReactNode, RefObject } from 'react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'
import { useFocusTrap, useLockBodyScroll } from '../lib/hooks'
import { Button } from './Button'
import { FeaturedIcon, type FeaturedIconColor } from './FeaturedIcon'
import { IconCheck, IconClose } from './icons'
import {
  type OverlayPhase,
  overlayTakesInput,
  useInitialFocus,
  useOverlayEscape,
  useOverlayPresence,
} from './overlay-layer'

export type ModalSize = 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'

/*
 * The widths Salesforge draws, measured. The names kept their order, so a call
 * site asking for `lg` still gets the wider of its neighbours, and two steps
 * were added above for the sender profile and the offer form.
 *
 *   sm 400   Add new user (402), Add label, Assign follow-up
 *   base 450 Workspace selector
 *   md 500   Connect any email provider
 *   lg 640   Mailbox settings, Bulk update (650)
 *   xl 800   Bulk connect mailboxes, Share as image
 *   2xl 960  Sender profile
 *   3xl 1060 Offer form
 *
 * The default stays `md`, which moved from 512 to 500. Below 640px every size
 * is the window less 32, which is the outer padding doing its job on `w-full`.
 */
const SIZES: Record<ModalSize, string> = {
  sm: 'max-w-[25rem]',
  base: 'max-w-[28.125rem]',
  md: 'max-w-[31.25rem]',
  lg: 'max-w-[40rem]',
  xl: 'max-w-[50rem]',
  '2xl': 'max-w-[60rem]',
  '3xl': 'max-w-[66.25rem]',
}

/**
 * The three header shapes, all measured.
 *
 * `stacked`: a 48 glyph tile at the top left, the title under it, the
 * description under that, and no rule. Add new user, Add label, every confirm.
 * `row`: a 40 outline tile beside the title and description, then a full-width
 * rule. Mailbox settings, Add webhook, Sender profile.
 * `title`: the title alone with the close, then a rule. Assign follow-up.
 */
export type ModalHeader = 'stacked' | 'row' | 'title'

/**
 * `full`: one primary across the whole width. `split`: two equal buttons,
 * secondary left and primary right. `end`: right aligned, secondary then
 * primary.
 */
export type ModalFooterLayout = 'full' | 'split' | 'end'

/** One row of the left gutter checklist. */
export interface ModalAsideItem {
  /** The id of the section in the body this row scrolls to. */
  id: string
  label: ReactNode
  complete: boolean
  /** Instead of scrolling to `id`, for a modal that pages its sections. */
  onSelect?: () => void
}

/** How long the exit plays. The scrim fades and the panel settles 8px. */
export const MODAL_EXIT_MS = 150

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  /**
   * A line icon for the header tile. A `row` header draws it in an outline
   * square, a `stacked` one in a light circle.
   */
  icon?: ReactNode
  description?: ReactNode
  children: ReactNode
  /** Buttons for the bottom bar, laid out by `footerLayout`. */
  footer?: ReactNode
  size?: ModalSize
  /**
   * Set false while a mutation is in flight, or while something shown once has
   * to be acknowledged. The backdrop and Escape stop closing and the header's
   * close greys out, so the only way out is the one the modal offers.
   */
  dismissible?: boolean
  className?: string
  /**
   * Which header to draw. Left out, a modal with an icon gets `row` and one
   * without gets `title`, which are the two shapes every existing call site
   * already had.
   */
  header?: ModalHeader
  /** The faded rings behind a `stacked` header's tile (Add tag). */
  rings?: boolean
  /** The colour family of a `stacked` header's tile. */
  iconTone?: FeaturedIconColor
  /** Left out, the footer is right aligned, which is how it always was. */
  footerLayout?: ModalFooterLayout
  /** A 220 wide checklist down the left, one row per section of the body. */
  aside?: readonly ModalAsideItem[]
  /** The aside row to highlight as the section in view. */
  asideCurrent?: string
}

export function Modal({
  open,
  onClose,
  title,
  icon,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className,
  header,
  rings,
  iconTone,
  footerLayout,
  aside,
  asideCurrent,
}: ModalProps) {
  const titleId = useId()
  const descriptionId = useId()
  const phase = useOverlayPresence(open, MODAL_EXIT_MS)
  const live = overlayTakesInput(phase)
  const containerRef = useFocusTrap<HTMLDivElement>(live)
  useInitialFocus(live, containerRef)
  useLockBodyScroll(phase !== 'closed')
  useOverlayEscape(live && dismissible, onClose)
  const bodyRef = useRef<HTMLDivElement>(null)
  const hiddenBelow = useHiddenBelow(bodyRef, live)

  if (phase === 'closed') return null

  return createPortal(
    <ModalFrame
      phase={phase}
      onClose={onClose}
      title={title}
      icon={icon}
      description={description}
      footer={footer}
      size={size}
      dismissible={dismissible}
      className={className}
      header={header}
      rings={rings}
      iconTone={iconTone}
      footerLayout={footerLayout}
      aside={aside}
      asideCurrent={asideCurrent}
      titleId={titleId}
      descriptionId={descriptionId}
      containerRef={containerRef}
      bodyRef={bodyRef}
      hiddenBelow={hiddenBelow}
    >
      {children}
    </ModalFrame>,
    document.body,
  )
}

/**
 * Whether the body has content scrolled out of sight under it, which is the one
 * case the footer draws a rule above itself.
 */
function useHiddenBelow(bodyRef: RefObject<HTMLDivElement>, active: boolean): boolean {
  const [hidden, setHidden] = useState(false)
  const measure = useCallback(() => {
    const body = bodyRef.current
    if (!body) return
    setHidden(body.scrollTop + body.clientHeight < body.scrollHeight - 1)
  }, [bodyRef])
  useEffect(() => {
    if (!active) return undefined
    const body = bodyRef.current
    if (!body) return undefined
    measure()
    body.addEventListener('scroll', measure, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(body)
    return () => {
      body.removeEventListener('scroll', measure)
      observer?.disconnect()
    }
  }, [active, bodyRef, measure])
  return hidden
}

// ---------------------------------------------------------------------------
// The painted half
// ---------------------------------------------------------------------------

export interface ModalFrameProps extends Omit<ModalProps, 'open'> {
  phase: OverlayPhase
  titleId: string
  descriptionId: string
  containerRef?: RefObject<HTMLDivElement>
  bodyRef?: RefObject<HTMLDivElement>
  /** Draws the rule over the footer while content is hidden under it. */
  hiddenBelow?: boolean
}

const EASE = 'cubic-bezier(0.2, 0.7, 0.3, 1)'

/** Inline, because a closing overlay changes these the moment it starts to go. */
export function modalExitStyles(phase: OverlayPhase): { scrim?: CSSProperties; panel?: CSSProperties } {
  if (phase !== 'closing') return {}
  return {
    scrim: { opacity: 0, transition: `opacity ${MODAL_EXIT_MS}ms ${EASE}` },
    panel: {
      opacity: 0,
      transform: 'translateY(8px)',
      transition: `opacity ${MODAL_EXIT_MS}ms ${EASE}, transform ${MODAL_EXIT_MS}ms ${EASE}`,
    },
  }
}

const FOOTER_LAYOUT: Record<ModalFooterLayout, string> = {
  full: 'flex flex-col gap-3 [&>*]:w-full',
  split: 'grid grid-cols-2 gap-3 [&>*]:w-full',
  end: 'flex flex-wrap items-center justify-end gap-3',
}

/**
 * The modal without its portal or its hooks, so it can be rendered on its own
 * and asserted on, which is the arrangement `MenuPanel` already uses.
 */
export function ModalFrame({
  phase,
  onClose,
  title,
  icon,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className,
  header,
  rings = false,
  iconTone = 'primary',
  footerLayout = 'end',
  aside,
  asideCurrent,
  titleId,
  descriptionId,
  containerRef,
  bodyRef,
  hiddenBelow = false,
}: ModalFrameProps) {
  const live = overlayTakesInput(phase)
  const shape: ModalHeader = header ?? (icon ? 'row' : 'title')
  const exit = modalExitStyles(phase)

  const heading = (
    <h2 id={titleId} className="text-title font-semibold text-ink">
      {title}
    </h2>
  )
  const describedBy = description ? (
    <p id={descriptionId} className="mt-1 text-base text-ink-muted">
      {description}
    </p>
  ) : null

  /*
   * The close is part of the header's shape, so it is always drawn and only
   * disabled while the modal refuses to be dismissed. It used to be dropped
   * from the markup instead, which left a modal over a mutation in flight with
   * nothing where every other modal has a close, and moved the header's right
   * edge as the request finished. Disabled leaves exactly the exits that were
   * there before: the backdrop and Escape are still refused, and a secret shown
   * once is still acknowledged through the footer.
   *
   * IT IS PLACED BY A WRAPPER, NOT BY THE BUTTON. `Button` carries `relative`
   * in its own base classes, `cn` is a plain joiner, and Tailwind emits
   * `.relative` after `.absolute`, so `absolute right-4 top-4` handed to the
   * button lost and the X fell into the flow under the header, at the left
   * edge of the body. The wrapper has no position of its own to fight with.
   * It sits inside each header, which is `relative`, so the X is pinned to the
   * header's top right corner whichever shape the header is.
   */
  const close = (
    <div data-modal-close="" className="absolute right-4 top-4">
      <Button
        variant="ghost"
        size="md"
        iconOnly
        onClick={onClose}
        disabled={!dismissible}
        aria-label="Close"
        className="group"
      >
        <IconClose className="!h-5 !w-5 text-ink-faint group-hover:text-ink-subtle" />
      </Button>
    </div>
  )

  return (
    <div
      data-overlay="modal"
      data-phase={phase}
      aria-hidden={live ? undefined : true}
      className={cn(
        'fixed inset-0 z-modal flex items-center justify-center p-4 sm:p-6',
        // The fix for the swallowed click. See overlay-layer.ts.
        !live && 'pointer-events-none',
      )}
    >
      <div
        data-overlay-scrim=""
        className="fixed inset-0 animate-scrim-in bg-overlay/70 backdrop-blur-[8px]"
        style={exit.scrim}
        onClick={live && dismissible ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        style={exit.panel}
        className={cn(
          'relative flex max-h-[calc(100dvh-48px)] w-full animate-scale-in overflow-hidden rounded-xl bg-surface shadow-xl',
          SIZES[size],
          className,
        )}
      >
        {aside && aside.length > 0 ? (
          <nav
            aria-label="Sections"
            className="hidden w-[13.75rem] shrink-0 flex-col gap-1 border-r border-line p-3 sm:flex"
          >
            {aside.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-current={item.id === asideCurrent ? 'true' : undefined}
                onClick={() => {
                  if (item.onSelect) item.onSelect()
                  else document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className={cn(
                  'flex h-10 items-center gap-3 rounded-md px-2.5 text-left text-base font-semibold text-ink-secondary transition hover:bg-surface-hover',
                  item.id === asideCurrent && 'bg-surface-selected',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'grid h-5 w-5 shrink-0 place-items-center rounded-full [&_svg]:!h-3 [&_svg]:!w-3',
                    item.complete ? 'bg-primary-600 text-ink-on' :'border-[1.5px] border-line-strong',
                  )}
                >
                  {item.complete ? <IconCheck /> : null}
                </span>
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                <span className="sr-only">{item.complete ? 'complete' : 'not complete'}</span>
              </button>
            ))}
          </nav>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col">
          {shape === 'stacked' ? (
            <div data-modal-header="stacked" className="relative shrink-0 px-6 pr-14 pt-6">
              {icon ? (
                // A flex box so the tile is a block, with no line box under it
                // to add a few pixels of descender space above the title.
                <div className="mb-4 flex">
                  <FeaturedIcon icon={icon} variant="light-circle" color={iconTone} size="lg" rings={rings} />
                </div>
              ) : null}
              {heading}
              {describedBy}
              {close}
            </div>
          ) : shape === 'row' ? (
            <div data-modal-header="row" className="relative flex shrink-0 items-start gap-4 border-b border-line px-6 pb-5 pr-14 pt-6">
              {icon ? <FeaturedIcon icon={icon} variant="outline-square" size="md" /> : null}
              <div className="min-w-0">
                {heading}
                {describedBy}
              </div>
              {close}
            </div>
          ) : (
            <div data-modal-header="title" className="relative shrink-0 border-b border-line px-6 py-5 pr-14">
              {heading}
              {describedBy}
              {close}
            </div>
          )}

          {/* A confirm can be all header and footer. An empty body would only
              add its padding between the description and the buttons. */}
          {children === null || children === undefined || children === false ? null : (
            <div
              ref={bodyRef}
              data-overlay-body=""
              className={cn('min-h-0 flex-1 overflow-y-auto px-6', shape === 'stacked' ? 'py-6' : 'py-5')}
            >
              {children}
            </div>
          )}

          {footer ? (
            <div
              data-overlay-footer=""
              data-layout={footerLayout}
              className={cn(
                'shrink-0 px-6 pb-6 pt-8',
                hiddenBelow ? 'border-t border-line' : 'border-t border-transparent',
                FOOTER_LAYOUT[footerLayout],
              )}
            >
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
